import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    AppconfigState,
    FontSizeType,
    InsperaObject,
    MicObject,
    SpeedObject,
    PageObject,
    SpeakCheckScreenDetailsObject,
    CalibrationDetails,
} from '../interface/interface';
import { Content } from '../interface/ContentType';

const initialState: AppconfigState = {
    testTaker: {
        testTakerId: '1234567890',
        testTakerName: 'Test Taker 1',
        testTakerUsername: 'TestTaker_Username',
        testTakerCenterName: 'London',
        testTakerLanguage: 'en_CA',
        testTakerAccessToken: '',
        testTakerSessionId: '',
    },
    precheckStartTimestamp: null,
    appTheme: 'black_on_white',
    appFontSize: 'sm',
    appAudioLevel: 100,
    appMicAccess: false,
    selectedMic: {
        deviceId: '',
        label: '',
    },
    silenceMeanValue: 0,
    insperaDetails: {
        assessmentId: '',
        externalId: '',
        partner: '',
        product: '',
        globalReferenceId: '',
    },
    fileSpeed: {
        uploadSpeed: '',
        downloadSpeed: '',
    },
    speakCheckScreenDetails: {
        duration: 0,
        responseFilePath: '',
    },
    pageObject: {
        pageIndex: 0,
        isSpeakCheckDone: false,
        disableSummaryScreenButton: false,
        isMicSelected: false,
        disableNextButton: true
    },
    soundCheckScreenAudio: false,
    isSilent: false,
    selectedTaskId: null,
    precheckApiError: '',
    calibrationDetails: {
        status: 'LIVE',
        level: '',
    },
    navigationStartTime: null,
    loadingDurations: {
        loginLoadTime: 0,
        finalFileUploadingTime: 0,
        testLoadingTime: 0,
        audiofilesLoadingTime: 0,
    },
    role: 'TESTTAKER',
    content: null,
};

export const appConfigSlice = createSlice({
    name: 'appConfigSlice',
    initialState,
    reducers: {
        resetStore: () => {
            return initialState;
        },
        changeTestTaker: (state, action: PayloadAction<any>) => {
            state.testTaker = { ...state.testTaker, ...action.payload };
        },
        changeAppFontSize: (state, action: PayloadAction<FontSizeType>) => {
            state.appFontSize = action.payload;
        },
        changeAppTheme: (state, action: PayloadAction<string>) => {
            state.appTheme = action.payload;
        },
        changeAppAudioLevel: (state, action: PayloadAction<number>) => {
            state.appAudioLevel = action.payload;
        },
        changeAppMicAccess: (state, action: PayloadAction<boolean>) => {
            state.appMicAccess = action.payload;
        },
        changeSelectedMic: (state, action: PayloadAction<MicObject>) => {
            state.selectedMic = action.payload;
        },
        changeSelectedTaskId: (state, action: PayloadAction<string | null>) => {
            state.selectedTaskId = action.payload;
        },
        changeSilenceValue: (state, action: PayloadAction<any>) => {
            state.silenceMeanValue = action.payload;
        },
        changeAccessTokenValue: (state, action: PayloadAction<any>) => {
            state.testTaker.testTakerAccessToken = action.payload;
        },
        changeTestTakerUsernameValue: (state, action: PayloadAction<any>) => {
            state.testTaker.testTakerUsername = action.payload;
        },
        changeTestTakerSessionIdValue: (state, action: PayloadAction<any>) => {
            state.testTaker.testTakerSessionId = action.payload;
        },
        changeInsperaDetails: (state, action: PayloadAction<InsperaObject>) => {
            state.insperaDetails = action.payload;
        },
        changePageDetails: (state, action: PayloadAction<PageObject>) => {
            state.pageObject = action.payload;
        },
        changeFileSpeed: (state, action: PayloadAction<SpeedObject>) => {
            state.fileSpeed = action.payload;
        },
        changeSpeakPrecheckDuration: (state, action: PayloadAction<SpeakCheckScreenDetailsObject>) => {
            state.speakCheckScreenDetails = action.payload;
        },
        changeSoundScreenAudio: (state, action: PayloadAction<boolean>) => {
            state.soundCheckScreenAudio = action.payload;
        },
        changeIsSilent: (state, action: PayloadAction<boolean>) => {
            state.isSilent = action.payload;
        },
        changePrecheckApiError: (state, action: PayloadAction<string>) => {
            state.precheckApiError = action.payload;
        },
        changeCalibrationDetails: (state, action: PayloadAction<CalibrationDetails>) => {
            state.calibrationDetails = action.payload;
        },
        setNavigationStartTime: (state, action) => {
            state.navigationStartTime = action.payload;
        },
        changeLoadingTimes: (state, action: PayloadAction<any>) => {
            state.loadingDurations = { ...state.loadingDurations, ...action.payload };
        },
        setPrecheckStartTimestamp: (state, action: PayloadAction<string>) => {
            state.precheckStartTimestamp = action.payload;
        },
        setContentData: (state, action: PayloadAction<Content>) => {
            state.content = action.payload;
        },
        setRole: (state, action: PayloadAction<'ASSESSMENT' | 'TESTTAKER'>) => {
            state.role = action.payload;
        },
        setNextButtonToDisable: (state, action: PayloadAction<boolean>) => { 
            state.pageObject.disableNextButton = action.payload;
        }
    },
});

export const {
    resetStore,
    changeAppFontSize,
    changeAppTheme,
    setPrecheckStartTimestamp,
    changeAppAudioLevel,
    changeAppMicAccess,
    changeSelectedMic,
    changeSelectedTaskId,
    changeSilenceValue,
    changeTestTaker,
    changeAccessTokenValue,
    changeTestTakerUsernameValue,
    changeTestTakerSessionIdValue,
    changeInsperaDetails,
    changePageDetails,
    changeFileSpeed,
    changeSpeakPrecheckDuration,
    changeSoundScreenAudio,
    changeIsSilent,
    changePrecheckApiError,
    changeCalibrationDetails,
    setNavigationStartTime,
    changeLoadingTimes,
    setContentData,
    setRole,
    setNextButtonToDisable
} = appConfigSlice.actions;

export default appConfigSlice.reducer;
